import type { AirportDto, FlightDto } from '@flightlog/shared';
import { useEffect, useState } from 'react';
import { ApiError, createFlight, getAirports } from '../api';
import { ChevronDownIcon } from '../icons';

interface Props {
  onClose: () => void;
  onCreated: (flight: FlightDto) => void;
}

function todayLocal(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.message === 'duplicate') return 'Такой рейс уже есть в журнале';
    if (error.message === 'unknown_airport') return 'Не знаю такой аэропорт — проверь код ИАТА';
    if (error.message === 'bad_flight_number') return 'Не понял номер рейса. Пример: SU 6332';
  }
  return 'Не получилось сохранить. Попробуй ещё раз.';
}

export function AddFlight({ onClose, onCreated }: Props) {
  const [airports, setAirports] = useState<AirportDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    flightNumber: '',
    depIata: '',
    arrIata: '',
    date: todayLocal(),
    depTime: '10:00',
    arrTime: '12:00',
    seat: '',
  });

  useEffect(() => {
    getAirports()
      .then(setAirports)
      .catch(() => setAirports([]));
  }, []);

  function update(field: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const flight = await createFlight({
        flightNumber: form.flightNumber,
        depIata: form.depIata.toUpperCase(),
        arrIata: form.arrIata.toUpperCase(),
        date: form.date,
        depTime: form.depTime,
        arrTime: form.arrTime,
        seat: form.seat || undefined,
      });
      onCreated(flight);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true">
      <button type="button" className="overlay-backdrop" aria-label="Закрыть" onClick={onClose} />
      <form className="sheet" onSubmit={submit}>
        <div className="sheet-grab" />
        <div className="sheet-top">
          <span className="eyebrow">Новый рейс</span>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            <ChevronDownIcon />
          </button>
        </div>

        <label className="field">
          <span>Номер рейса</span>
          <input
            value={form.flightNumber}
            onChange={(event) => update('flightNumber', event.target.value.toUpperCase())}
            placeholder="SU 6332"
            autoComplete="off"
            required
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span>Откуда</span>
            <input
              value={form.depIata}
              onChange={(event) => update('depIata', event.target.value.toUpperCase())}
              placeholder="SVO"
              list="airport-list"
              maxLength={3}
              required
            />
          </label>
          <label className="field">
            <span>Куда</span>
            <input
              value={form.arrIata}
              onChange={(event) => update('arrIata', event.target.value.toUpperCase())}
              placeholder="LED"
              list="airport-list"
              maxLength={3}
              required
            />
          </label>
        </div>

        <label className="field">
          <span>Дата вылета</span>
          <input
            type="date"
            value={form.date}
            onChange={(event) => update('date', event.target.value)}
            required
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span>Вылет</span>
            <input
              type="time"
              value={form.depTime}
              onChange={(event) => update('depTime', event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>Прилёт</span>
            <input
              type="time"
              value={form.arrTime}
              onChange={(event) => update('arrTime', event.target.value)}
              required
            />
          </label>
        </div>

        <label className="field">
          <span>Место (необязательно)</span>
          <input
            value={form.seat}
            onChange={(event) => update('seat', event.target.value.toUpperCase())}
            placeholder="12A"
            maxLength={4}
          />
        </label>

        <datalist id="airport-list">
          {airports.map((airport) => (
            <option key={airport.iata} value={airport.iata}>
              {airport.city} · {airport.name}
            </option>
          ))}
        </datalist>

        {error ? <p className="form-error">{error}</p> : null}

        <button type="submit" className="button button-primary" disabled={busy}>
          {busy ? 'Сохраняю…' : 'Сохранить рейс'}
        </button>
      </form>
    </div>
  );
}
